// Seat-map data helpers for pages that reuse CompareSeatMap (Lion Auto).
// Copies of Compare.js's inline versions; Compare keeps its own copies until
// a later increment moves it over to these.
import { normalizeGroupColor } from './groupColors';

// The chartData prop CompareSeatMap expects, from the /venue-data payload.
// Callers memoize on venueData: a new object resets the map's zoom.
export function buildChartData(venueData) {
  if (!venueData?.elements) return null;
  return {
    width: venueData.chartWidth,
    height: venueData.chartHeight,
    elements: venueData.elements,
    sections: venueData.sections,
    groups: venueData.groups,
    event: venueData.event,
  };
}

// Chart section ids someone is selling in (ours + competitors), so the map
// can grey out the rest. From the live pricing payload, not the 30-min venue
// cache. Listings without a sectionId match by exact display name. null
// until pricing loads, so nothing greys out early. Section ids are strings,
// the way the map keys them.
export function availableSectionIdsFrom(pricingData, chartData) {
  if (!pricingData) return null;
  const ids = new Set();
  let idsByName = null;
  for (const l of [...(pricingData.myInventory || []), ...(pricingData.competitorListings || [])]) {
    if (l.sectionId) { ids.add(String(l.sectionId)); continue; }
    const name = (l.displayName || l.section || '').toLowerCase().trim();
    if (!name || !chartData?.sections) continue;
    if (!idsByName) {
      idsByName = new Map();
      for (const [id, sec] of Object.entries(chartData.sections)) {
        const n = (sec?.name || '').toLowerCase().trim();
        if (!n) continue;
        if (!idsByName.has(n)) idsByName.set(n, []);
        idsByName.get(n).push(id);
      }
    }
    for (const id of idsByName.get(name) || []) ids.add(id);
  }
  return ids;
}

// Zones (chart groups) with their sections, for the zone list beside the
// map. Colors come from the pricing payload's sectionGroups, matched by
// NAME: chart group ids are not Skybox group ids.
export function chartGroupsFrom(chartData, sectionGroups = []) {
  if (!chartData || !chartData.groups || !chartData.sections || !chartData.elements) return [];

  const { elements, sections, groups } = chartData;
  const groupMap = new Map();

  elements.forEach(el => {
    if (el.type !== 'path' || !el.id) return;
    const sectionInfo = sections[el.id];
    if (!sectionInfo) return;

    const groupId = sectionInfo.groupId;
    const groupName = groups[groupId];
    if (!groupName) return;

    if (!groupMap.has(groupId)) {
      const apiGroup = (sectionGroups || []).find(g =>
        g.name?.toLowerCase() === groupName?.toLowerCase()
      );
      groupMap.set(groupId, {
        id: groupId,
        name: groupName,
        color: normalizeGroupColor(apiGroup?.color) || '#888888',
        sections: [],
      });
    }

    groupMap.get(groupId).sections.push({
      id: el.id,
      name: sectionInfo.name,
      group: groupName,
      lowestPrice: sectionInfo.lowestPrice,
      highestPrice: sectionInfo.highestPrice,
    });
  });

  return Array.from(groupMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}
